import {
  FC,
  forwardRef,
  Fragment,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Route } from "nextjs-routes";
import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import useNewMessagingEnabled from "@src/features/messaging/posts/useNewMessagingEnabled";
import OptInConfirm from "./OptInConfirm";
import OptInPowerUp from "./OptInPowerUp";
import useCanOptIntoRelease from "./useCanOptIntoRelease";
import Sparkle3OutlineIcon from "@src/lib/icons/18px/sparkle-3-outline";
import { UserType } from "@src/types.generated";
import {
  ReleaseAudience,
  ReleaseHeadlineRelease,
  ReleaseLetterCopy,
  ReleaseLetterStory,
  trackedReleaseUrl,
} from "@src/features/home/releaseHeadline/releases";
import {
  ReleaseStep,
  trackRelease,
} from "@src/features/home/releaseHeadline/releaseTracking";
import useVisibleRelease from "@src/features/home/releaseHeadline/useVisibleRelease";
import { useAnnouncementLetterStore } from "./useAnnouncementLetterStore";

// The release letter tucked into the bottom of the admin rail: a sealed
// envelope card that, opened, sends the letter out of the envelope and across
// the screen into a letter in the middle of it.

// opening: the flap folds back and the letter slides out of the envelope.
// flying: the letter grows from the envelope into the open letter.
// landing: the letter dialog shows under the landed sheet, which fades away.
type Phase = "idle" | "opening" | "flying" | "landing";

// The flap and the slide-out overlap, so the letter is out by OPEN_MS.
const FLAP_S = 0.28;
const SLIDE_DELAY_S = 0.1;
const OPEN_MS = 420;
const FLY_S = 0.55;
// How many times an unread envelope wiggles after the page loads.
const NUDGES = 3;
const LAND_S = 0.18;
// A soft ease-out, quick off the mark and settling gently into place.
const SETTLE = [0.32, 0.72, 0, 1] as const;
// Once read, the letter stays in the nav this long so people can reopen it.
export const KEEP_AFTER_READ_MS = 3 * 24 * 60 * 60 * 1000;

const isKeptLongEnough = (readAt: string): boolean =>
  readAt !== "" && Date.now() - Date.parse(readAt) > KEEP_AFTER_READ_MS;

// The rail remounts on every page, so "shown" goes out once per release per
// page load of the app, not once per navigation.
const shownReleases = new Set<string>();

type RailProps = {
  expanded: boolean;
  // Tells the rail the letter is opening or open, so an unpinned rail stays
  // expanded behind it (like the New menu) and the envelope stays put.
  onOpenChange?: (open: boolean) => void;
};

// Mounted in the admin rail; renders nothing unless the live release uses
// the nav letter.
const AnnouncementLetter: FC<RailProps> = (props) => {
  const visible = useVisibleRelease();
  if (
    !visible ||
    visible.experience !== "nav-letter" ||
    !visible.release.letter
  ) {
    return null;
  }
  return (
    <AnnouncementLetterCard
      key={visible.release.slug}
      release={visible.release}
      letter={visible.release.letter}
      audience={visible.audience}
      isPreview={visible.isPreview}
      {...props}
    />
  );
};

const AnnouncementLetterCard: FC<
  {
    release: ReleaseHeadlineRelease;
    letter: ReleaseLetterCopy;
    audience: ReleaseAudience;
    isPreview: boolean;
  } & RailProps
> = ({ release, letter, audience, isPreview, expanded, onOpenChange }) => {
  const storedReadAt = useAnnouncementLetterStore(
    (state) => state.readAt[release.slug] ?? "",
  );
  const markStoredRead = useAnnouncementLetterStore((state) => state.markRead);
  // Previews come back unread on every load without touching storage.
  const [previewReadAt, setPreviewReadAt] = useState("");
  const readAt = isPreview ? previewReadAt : storedReadAt;
  const read = readAt !== "";
  const [phase, setPhase] = useState<Phase>("idle");
  const [letterOpen, setLetterOpen] = useState(false);
  const [flightFrom, setFlightFrom] = useState<DOMRect | null>(null);
  const envelopeRef = useRef<HTMLButtonElement>(null);
  const innerLetterRef = useRef<HTMLSpanElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const canOptIn = useCanOptIntoRelease(letter.optIn?.flags);
  const reduceMotion = useReducedMotion();
  const track = (step: ReleaseStep, context: Record<string, string> = {}) =>
    trackRelease(
      release,
      "nav-letter",
      step,
      { audience, ...context },
      isPreview,
    );

  useEffect(() => {
    // A letter that has left the nav isn't shown, so it isn't counted.
    const gone = isKeptLongEnough(readAt);
    if (!isPreview && !gone && !shownReleases.has(release.slug)) {
      shownReleases.add(release.slug);
      track("shown", { read: String(read) });
    }
    return () => window.clearTimeout(openTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  // Report only real changes, and release the rail if unmounted while open.
  const busy = phase !== "idle" || letterOpen;
  const reportedOpen = useRef(false);
  useEffect(() => {
    if (busy === reportedOpen.current) return;
    reportedOpen.current = busy;
    onOpenChange?.(busy);
  }, [busy, onOpenChange]);
  useEffect(
    () => () => {
      if (reportedOpen.current) onOpenChange?.(false);
    },
    [onOpenChange],
  );

  // The dialog mounts hidden under the flight, where it can't take focus;
  // once the letter lands, move focus into it so keyboard and screen
  // reader users arrive in the letter.
  useEffect(() => {
    if (phase !== "landing") return;
    // Its visibility comes back over a few frames (the dialog shell
    // transitions it), and a hidden element can't take focus; try each frame
    // for a moment.
    let frame = 0;
    let tries = 30;
    const focusLetter = () => {
      const dialog = dialogRef.current;
      if (!dialog || dialog.contains(document.activeElement)) return;
      dialog.focus();
      if (document.activeElement !== dialog && --tries > 0)
        frame = requestAnimationFrame(focusLetter);
    };
    frame = requestAnimationFrame(focusLetter);
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  const markRead = () => {
    if (isPreview) setPreviewReadAt(new Date().toISOString());
    else markStoredRead(release.slug);
  };

  const openLetter = () => {
    if (phase !== "idle" || letterOpen) return;
    track("opened", { was_read: String(read) });
    markRead();
    // Tell the rail now, in the same render as the dialog: a letter that
    // opens at once takes focus before an effect could, and the rail would
    // read that blur as the pointer leaving.
    reportedOpen.current = true;
    onOpenChange?.(true);
    if (reduceMotion) {
      setLetterOpen(true);
      return;
    }
    setPhase("opening");
    openTimer.current = window.setTimeout(() => {
      // The flight starts exactly where the slid-out letter is, and the
      // letter dialog mounts now (still invisible) so the flight can land on
      // its box.
      setFlightFrom(innerLetterRef.current?.getBoundingClientRect() ?? null);
      setPhase("flying");
      setLetterOpen(true);
    }, OPEN_MS);
  };

  const flapOpen = phase === "opening";
  const nudge = !read && phase === "idle" && !reduceMotion;
  // Unread, the card is a larger version of itself so it can't be missed. It
  // stays large while the letter is out, then settles into the compact card
  // once the letter is closed.
  const large = expanded && (!read || busy);
  if (!isPreview && isKeptLongEnough(readAt) && !busy) return null;

  return (
    <>
      <button
        ref={envelopeRef}
        type="button"
        onClick={openLetter}
        aria-label={`${letter.title}: ${read ? "read it again" : "see what’s new (unread)"}`}
        data-testid="release-nav-letter"
        data-phase={phase}
        className={cn(
          "flex shrink-0 cursor-pointer rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-state-focus",
          !expanded && "size-8 items-center justify-center",
          expanded && "w-full border shadow-default transition-colors",
          large
            ? "flex-col items-start gap-3 p-3"
            : expanded && "items-center gap-2 px-2 py-2.5",
          // Unread, it wears the brand tint so it stands out in the nav.
          expanded &&
            (read
              ? "border-border-primary bg-bg-primary hover:bg-state-hover"
              : "border-brand-border bg-brand-bg hover:bg-bg-primary"),
        )}
      >
        <span
          className={cn("relative shrink-0", large ? "h-9 w-12" : "h-6 w-8")}
        >
          <span
            className={cn(
              "absolute top-0 left-0 origin-top-left",
              large && "scale-150",
            )}
          >
            <motion.span
              className="relative block h-6 w-8"
              style={{ perspective: 200 }}
              // Unread, the envelope gives a little wiggle a few times after the
              // page loads, then stays still.
              animate={
                nudge
                  ? {
                      rotate: [0, -14, 12, -9, 6, 0],
                      y: [0, -4, 0, -2, 0, 0],
                      scale: [1, 1.15, 1.15, 1.08, 1, 1],
                    }
                  : { rotate: 0, y: 0, scale: 1 }
              }
              transition={
                nudge
                  ? {
                      duration: 0.9,
                      ease: "easeInOut",
                      delay: 1,
                      repeat: NUDGES - 1,
                      repeatDelay: 4,
                    }
                  : { duration: 0.2 }
              }
            >
              {/* Back of the envelope */}
              <span className="absolute inset-0 rounded-sm border border-border-primary bg-bg-secondary" />

              {/* The letter inside: rises out as the flap opens */}
              <motion.span
                ref={innerLetterRef}
                aria-hidden
                className="absolute inset-x-1 top-0.5 bottom-0.5 flex flex-col gap-1 rounded-sm border border-border-secondary bg-bg-elevated px-1 pt-1"
                initial={{ opacity: 0 }}
                animate={
                  phase === "opening"
                    ? { y: -16, opacity: 1 }
                    : // Sealed otherwise: no letter shows past the flap's edges.
                      // Once flying, the flight takes over in the same frame.
                      { y: 0, opacity: 0 }
                }
                transition={
                  phase === "opening"
                    ? {
                        duration: OPEN_MS / 1000 - SLIDE_DELAY_S,
                        delay: SLIDE_DELAY_S,
                        ease: SETTLE,
                      }
                    : { duration: 0 }
                }
              >
                <PaperLines count={2} />
              </motion.span>

              {/* Front: covers the whole envelope so nothing inside shows */}
              <svg
                aria-hidden
                viewBox="0 0 40 28"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full overflow-visible"
              >
                <path
                  d="M2 0.5 L38 0.5 Q39.5 0.5 39.5 2 L39.5 26 Q39.5 27.5 38 27.5 L2 27.5 Q0.5 27.5 0.5 26 L0.5 2 Q0.5 0.5 2 0.5 Z"
                  vectorEffect="non-scaling-stroke"
                  className="fill-bg-secondary stroke-border-primary"
                />
                <path
                  d="M1 27 L16.5 17 M39 27 L23.5 17"
                  vectorEffect="non-scaling-stroke"
                  className="fill-none stroke-border-primary"
                />
              </svg>

              {/* Flap: hinged on the top edge, folds back when opened */}
              <motion.svg
                aria-hidden
                viewBox="0 0 40 18"
                preserveAspectRatio="none"
                className="absolute inset-x-0 top-0 h-4 w-full origin-top overflow-visible"
                initial={false}
                animate={{ rotateX: flapOpen ? 180 : 0 }}
                transition={{ duration: FLAP_S, ease: [0.4, 0, 0.2, 1] }}
              >
                <path
                  d="M2 0.5 L38 0.5 Q39.5 0.5 38.6 1.6 L21.2 16.6 Q20 17.6 18.8 16.6 L1.4 1.6 Q0.5 0.5 2 0.5 Z"
                  vectorEffect="non-scaling-stroke"
                  className="fill-bg-secondary stroke-border-primary"
                />
              </motion.svg>

              {/* Wax seal at the flap's tip */}
              {!read && (
                <span
                  data-testid="release-nav-letter-unread"
                  className="absolute inset-x-0 top-2.5 flex justify-center"
                >
                  {nudge && (
                    <motion.span
                      aria-hidden
                      className="absolute size-2 rounded-full bg-status-error-solid"
                      animate={{ scale: [1, 2.4], opacity: [0.5, 0] }}
                      transition={{
                        duration: 1.6,
                        ease: "easeOut",
                        repeat: Infinity,
                        repeatDelay: 0.6,
                      }}
                    />
                  )}
                  <motion.span
                    aria-hidden
                    className="size-2 rounded-full bg-status-error-solid ring-2 ring-bg-primary"
                    initial={false}
                    animate={{
                      opacity: flapOpen ? 0 : 1,
                      scale: flapOpen ? 0.4 : 1,
                    }}
                  />
                </span>
              )}
            </motion.span>
          </span>
        </span>

        {expanded && (
          <span className="flex w-full min-w-0 flex-1 flex-col items-start text-left">
            <Text
              variant={large ? "compact-title" : "fine-print-title"}
              className="w-full truncate text-text-primary"
            >
              {letter.title}
            </Text>
            {large ? (
              <>
                <Text
                  variant="fine-print-description"
                  multiline
                  className="mt-0.5 text-text-secondary"
                >
                  {letter.optIn && canOptIn
                    ? letter.optIn.teaser
                    : letter.teaser[audience]}
                </Text>
                <Text
                  variant="fine-print-title"
                  className="mt-2.5 text-brand-text"
                >
                  See what&apos;s new →
                </Text>
              </>
            ) : (
              <Text
                variant="fine-print-description"
                className="w-full truncate text-text-tertiary"
              >
                Read it again
              </Text>
            )}
          </span>
        )}
      </button>

      {(phase === "flying" || phase === "landing") &&
        flightFrom &&
        createPortal(
          <LetterFlight
            from={flightFrom}
            target={dialogRef}
            landed={phase === "landing"}
            onLanded={() => setPhase("landing")}
            onGone={() => setPhase("idle")}
          />,
          document.body,
        )}

      <LetterDialog
        ref={dialogRef}
        open={letterOpen}
        arriving={phase === "flying"}
        release={release}
        letter={letter}
        audience={audience}
        track={track}
        returnFocusTo={envelopeRef}
        onOpenChange={(open) => {
          if (open) return;
          track("closed");
          window.clearTimeout(openTimer.current);
          setLetterOpen(false);
          setPhase("idle");
        }}
      />
    </>
  );
};

const PaperLines: FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <span
        key={i}
        className={cn(
          "block h-0.5 rounded-full bg-border-secondary",
          i === count - 1 ? "w-1/2" : "w-full",
        )}
      />
    ))}
  </>
);

// The letter on its way out: a sheet of paper that starts as the small letter
// slid out of the envelope and grows, in one eased move, into the open letter's
// box. It lands exactly on the (still hidden) dialog, which then shows in its
// place, so there is no second entrance animation to cut to.
type Box = { left: number; top: number; width: number; height: number };

const LetterFlight: FC<{
  from: DOMRect;
  target: RefObject<HTMLDivElement | null>;
  // Once landed the dialog shows beneath it; it fades out over the dialog
  // rather than vanishing, so no frame is left empty in between.
  landed: boolean;
  onLanded: () => void;
  onGone: () => void;
}> = ({ from, target, landed, onLanded, onGone }) => {
  const [to, setTo] = useState<Box | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // The dialog portals in a frame or two after it opens; wait for it. Its
  // box comes from its layout size (centred in the viewport, as the dialog
  // shell places it), not getBoundingClientRect, which would catch it
  // part-way through its own zoom-in.
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      const el = target.current;
      if (el && el.offsetWidth > 0) {
        setTo({
          left: (window.innerWidth - el.offsetWidth) / 2,
          top: (window.innerHeight - el.offsetHeight) / 2,
          width: el.offsetWidth,
          height: el.offsetHeight,
        });
      } else frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  const box = ({ left, top, width, height }: Box) => ({
    left,
    top,
    width,
    height,
  });
  return (
    <motion.div
      ref={sheetRef}
      aria-hidden
      className="pointer-events-none fixed z-50 flex flex-col gap-1 overflow-hidden border border-border-primary bg-bg-elevated px-1 pt-1 shadow-extra-large"
      initial={{ ...box(from), borderRadius: 2, rotate: 0 }}
      animate={
        to
          ? {
              ...box(to),
              borderRadius: 16,
              // A slight sway in flight, upright at both ends.
              rotate: landed ? 0 : [0, -2, 0],
              opacity: landed ? 0 : 1,
            }
          : { ...box(from), borderRadius: 2, rotate: 0, opacity: 1 }
      }
      transition={
        landed
          ? { duration: LAND_S, ease: "easeOut" }
          : { duration: FLY_S, ease: SETTLE }
      }
      // Act on the animation that finished, not the current props: the
      // initial hold at `from` can complete after `to` is already known.
      onAnimationComplete={(definition) => {
        const done = definition as { left?: number; opacity?: number };
        if (done.opacity === 0) {
          // Keep it invisible through its last frame before unmounting.
          if (sheetRef.current) sheetRef.current.style.opacity = "0";
          onGone();
        } else if (to && done.left === to.left) onLanded();
      }}
    >
      <motion.span
        className="flex flex-col gap-1"
        initial={{ opacity: 1 }}
        animate={{ opacity: to ? 0 : 1 }}
        transition={{ duration: FLY_S / 3 }}
      >
        <PaperLines count={2} />
      </motion.span>
    </motion.div>
  );
};

const CTA_LINK =
  "inline-flex items-center gap-1 font-medium-v4 text-s-v4 text-brand-text hover:underline";

// Orgs already on Newsfeed go to it; otherwise Admins go to its setup.
// Managers get no link.
const NewsfeedCta: FC<{
  audience: ReleaseAudience;
  onFollow: () => void;
}> = ({ audience, onFollow }) => {
  const onNewsfeed = useNewMessagingEnabled();
  const link: { label: string; href: Route } | null = onNewsfeed
    ? { label: "Open Newsfeed", href: { pathname: "/newsfeed" } }
    : audience === UserType.Admin
      ? {
          label: "Turn on Newsfeed and Messages",
          href: { pathname: "/comms-opt-in" },
        }
      : null;
  if (!link) return null;
  return (
    <Link
      href={link.href}
      onClick={(event) => {
        // A link opened in another tab leaves the letter up.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        onFollow();
      }}
      className={CTA_LINK}
    >
      {link.label} →
    </Link>
  );
};

// Before launch, the footer's primary button: Admins who can change org
// settings opt their company in early. Everyone else gets no button.
const OptInButton: FC<{
  label: string;
  canOptIn: boolean;
  onStart: () => void;
}> = ({ label, canOptIn, onStart }) =>
  canOptIn ? (
    <Button data-testid="release-nav-letter-opt-in" onClick={onStart}>
      {label}
    </Button>
  ) : null;

const PageLink: FC<{
  href: string;
  onClick: () => void;
  children: ReactNode;
}> = ({ href, onClick, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className={CTA_LINK}
  >
    {children}
    <span className="sr-only"> (opens in a new tab)</span>
  </a>
);

// One row of the letter: an icon tile, then a text column every row shares,
// so the stories and "Also new" line up on the same left edge.
const LetterRow: FC<{
  icon: ReleaseLetterStory["icon"];
  eyebrow?: string;
  title: string;
  testId: string;
  children: ReactNode;
}> = ({ icon: Icon, eyebrow, title, testId, children }) => (
  <li
    data-testid={testId}
    className="flex gap-4 border-t border-border-secondary py-4 first:border-t-0 first:pt-0 last:pb-0"
  >
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-bg text-brand-text">
      <Icon />
    </span>
    <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 pt-1">
      {eyebrow && (
        <Text variant="fine-print-title" className="text-brand-text">
          {eyebrow}
        </Text>
      )}
      <Text variant="body-title" className="text-text-primary">
        {title}
      </Text>
      {children}
    </div>
  </li>
);

const forAudience = <T extends { audience?: ReleaseAudience[] }>(
  items: T[],
  audience: ReleaseAudience,
): T[] =>
  items.filter((item) => !item.audience || item.audience.includes(audience));

const LetterBody: FC<{
  release: ReleaseHeadlineRelease;
  letter: ReleaseLetterCopy;
  audience: ReleaseAudience;
  track: (step: ReleaseStep, context?: Record<string, string>) => void;
  onInAppCta: () => void;
}> = ({ release, letter, audience, track, onInAppCta }) => {
  const stories = forAudience(letter.stories, audience);
  const alsoNew = forAudience(letter.alsoNew, audience);
  const follow = (story: ReleaseLetterStory) =>
    track("feature_cta", { feature: story.slug });
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {stories.map((story) => (
        <LetterRow
          key={story.slug}
          testId={`release-nav-letter-story-${story.slug}`}
          icon={story.icon}
          eyebrow={story.eyebrow}
          title={story.title}
        >
          <Text
            variant="compact-description"
            multiline
            className="text-text-secondary"
          >
            {story.deck}
          </Text>
          {!story.link ? null : story.link === "newsfeed" ? (
            <NewsfeedCta
              audience={audience}
              onFollow={() => {
                follow(story);
                onInAppCta();
              }}
            />
          ) : (
            <PageLink
              href={`${trackedReleaseUrl(release, "nav-letter")}#${story.slug}`}
              onClick={() => follow(story)}
            >
              {story.link.label} →
            </PageLink>
          )}
        </LetterRow>
      ))}
      {alsoNew.length > 0 && (
        <LetterRow
          testId="release-nav-letter-also-new"
          icon={Sparkle3OutlineIcon}
          title={letter.alsoNewTitle ?? "Also new"}
        >
          {/* Hanging bullets, so a wrapped line tucks under its own text */}
          <ul className="m-0 flex list-disc flex-col gap-2.5 pl-4 marker:text-icon-tertiary">
            {alsoNew.map((item) => (
              <li
                key={item.slug}
                data-testid={`release-nav-letter-also-${item.slug}`}
                className="pl-0.5 text-pretty"
              >
                <Text
                  variant="compact-description"
                  multiline
                  className="text-text-secondary"
                >
                  <span className="font-medium-v4 text-text-primary">
                    {item.name}.
                  </span>{" "}
                  {item.line}
                </Text>
              </li>
            ))}
          </ul>
        </LetterRow>
      )}
    </ul>
  );
};

// Seconds of pen time per character, and the pause between lines.
const WRITE_S_PER_CHAR = 0.045;
const LINE_PAUSE_S = 0.15;
// Generous vertical inset so loops and descenders aren't cut.
const WRITTEN = "inset(-30% -10% -30% -10%)";
const UNWRITTEN = "inset(-30% 100% -30% -10%)";
const writeTime = (lines: readonly string[]) =>
  lines.reduce(
    (total, line) => total + line.length * WRITE_S_PER_CHAR + LINE_PAUSE_S,
    0,
  );

// Handwritten lines that write themselves in, one after another, once the
// letter has landed: each line is wiped in left to right at pen speed.
const Handwriting = ({
  lines,
  writing,
  startAfter = [],
  className,
}: {
  lines: readonly string[];
  writing: boolean;
  // Lines written before these, so a later block waits its turn.
  startAfter?: readonly string[];
  className?: string;
}) => {
  const reduceMotion = useReducedMotion();
  let delay = LAND_S + writeTime(startAfter);
  return (
    <p
      className={cn(
        "m-0 -rotate-2 font-[family-name:var(--font-family-handwriting)] text-xl-v4 leading-snug text-text-primary",
        className,
      )}
    >
      {lines.map((line, i) => {
        const duration = line.length * WRITE_S_PER_CHAR;
        const lineDelay = delay;
        delay += duration + LINE_PAUSE_S;
        return (
          <Fragment key={line}>
            {i > 0 && <br />}
            <motion.span
              className="inline-block"
              initial={reduceMotion ? false : { clipPath: UNWRITTEN }}
              animate={{
                clipPath: writing || reduceMotion ? WRITTEN : UNWRITTEN,
              }}
              transition={
                writing && !reduceMotion
                  ? { duration, delay: lineDelay, ease: "linear" }
                  : { duration: 0 }
              }
            >
              {line}
            </motion.span>
          </Fragment>
        );
      })}
    </p>
  );
};

// The full letter: letterhead, the release's heading addressed by hand to
// the reader, its stories and "Also new", and a handwritten sign-off.
const LetterDialog = forwardRef<
  HTMLDivElement,
  {
    open: boolean;
    // While the letter is still flying in, the dialog is laid out but
    // hidden; the flight lands on its box and it takes over.
    arriving: boolean;
    release: ReleaseHeadlineRelease;
    letter: ReleaseLetterCopy;
    audience: ReleaseAudience;
    track: (step: ReleaseStep, context?: Record<string, string>) => void;
    // The envelope, which gets focus back when the letter closes.
    returnFocusTo: RefObject<HTMLButtonElement | null>;
    onOpenChange: (open: boolean) => void;
  }
>(
  (
    {
      open,
      arriving,
      release,
      letter,
      audience,
      track,
      returnFocusTo,
      onOpenChange,
    },
    ref,
  ) => {
    const close = () => onOpenChange(false);
    const letterRef = useRef<HTMLDivElement>(null);
    const canOptIn = useCanOptIntoRelease(letter.optIn?.flags);
    // Get early access → what will happen → the power-up.
    const [optIn, setOptIn] = useState<
      "idle" | "confirming" | "powering" | "failed"
    >("idle");
    // The letter stays open while the opt-in saves and celebrates, so the
    // reload that follows always happens; closing otherwise starts over.
    const handleOpenChange = (next: boolean) => {
      if (!next && optIn === "powering") return;
      if (!next) setOptIn("idle");
      onOpenChange(next);
    };
    // Behind an opt-in step, the letter is out of reach of focus and readers.
    // Back from a step, focus returns to the button that opened it.
    const wasOptingIn = useRef(false);
    useEffect(() => {
      letterRef.current?.toggleAttribute("inert", optIn !== "idle");
      if (optIn === "idle" && wasOptingIn.current) {
        letterRef.current
          ?.querySelector<HTMLElement>(
            '[data-testid="release-nav-letter-opt-in"]',
          )
          ?.focus({ preventScroll: true });
      }
      wasOptingIn.current = optIn !== "idle";
    }, [optIn]);
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          ref={ref}
          hideDefaultTitle
          data-testid="release-nav-letter-dialog"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocusTo.current?.focus();
          }}
          className={cn(
            "flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0",
            // Hidden, not transparent: its zoom-in plays out unseen during the
            // flight, and it shows in the same frame the flight is removed.
            arriving && "invisible",
          )}
        >
          <motion.div
            ref={letterRef}
            initial={false}
            animate={{ opacity: arriving ? 0 : 1 }}
            transition={{ duration: arriving ? 0 : LAND_S, ease: "easeOut" }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-12 pt-8 pb-8">
              {/* Letterhead */}
              <div className="-mt-2 flex justify-center">
                <Image
                  src="/images/opus-wordmark.svg"
                  alt="Opus"
                  width={58}
                  height={16}
                  className="dark:invert"
                />
              </div>

              <DialogTitle size="large" className="mt-2 text-2xl-v4">
                {letter.headline[audience]}
              </DialogTitle>

              <LetterBody
                release={release}
                letter={letter}
                audience={audience}
                track={track}
                onInAppCta={close}
              />

              {/* Signed by hand on the letter's text column, under the bullets */}
              <Handwriting
                className="ml-12 whitespace-nowrap"
                writing={open && !arriving}
                lines={letter.signOff}
              />
            </div>

            {/* The way into the full release, and before launch the opt-in */}
            <DialogFooter className="shrink-0 border-t border-border-secondary px-12 py-4">
              <Button asChild variant={letter.optIn ? "outline" : "default"}>
                <a
                  href={trackedReleaseUrl(release, "nav-letter")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="release-nav-letter-full-release"
                  onClick={() => track("full_release")}
                >
                  View full release notes
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
              {letter.optIn && (
                <OptInButton
                  label={letter.optIn.label}
                  canOptIn={canOptIn}
                  onStart={() => {
                    track("opt_in");
                    setOptIn("confirming");
                  }}
                />
              )}
            </DialogFooter>
          </motion.div>
          {optIn === "confirming" && letter.optIn && (
            <OptInConfirm
              release={release}
              excludeSlugs={letter.optIn.excludeSlugs}
              onBack={() => setOptIn("idle")}
              onFeatureClick={(slug) =>
                track("feature_cta", { feature: slug, from: "early_access" })
              }
              onConfirm={() => {
                track("opt_in_confirmed");
                setOptIn("powering");
              }}
            />
          )}
          {(optIn === "powering" || optIn === "failed") && letter.optIn && (
            <OptInPowerUp
              release={release}
              excludeSlugs={letter.optIn.excludeSlugs}
              onFailed={() => {
                track("opt_in_failed");
                setOptIn("failed");
              }}
              onRetry={() => setOptIn("powering")}
              onBack={() => setOptIn("idle")}
              onDone={() => {
                track("opt_in_done");
                window.location.reload();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  },
);
LetterDialog.displayName = "LetterDialog";

export default AnnouncementLetter;
